import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { ColumnOption, ColumnUIKey } from "../../../constants/schema.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { textInRange } from "../../../utils/validation.js";
import { createAction } from "../../../r-html/dist/store/index.js";
import { relinkDomainColumn } from "../dictionary/domainLinks.js";
import { getLiveDomainName } from "../dictionary/membership.js";
import { addAndSort } from "../../../utils/collection/sequence.js";
import { createTable } from "../../../utils/collection/table.entity.js";
import { createColumn } from "../../../utils/collection/tableColumn.entity.js";
import { ActionType } from "./actions.js";
//#region src/engine/modules/table-column/atom.actions.ts
var addColumnAction = createAction(ActionType.addColumn);
var addColumn = ({ collections, lww }, { payload: { id, tableId }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const tableCollection = query(collections).collection("tableEntities");
	const table = tableCollection.getOrCreate(tableId, (id) => createTable({ id }));
	query(collections).collection("tableColumnEntities").addOne(createColumn({
		id,
		tableId
	})).addOperator(lww, safeVersion, id, () => {
		if (!arrayHas(table.columnIds)(id)) tableCollection.updateOne(tableId, (table) => {
			addAndSort(table.columnIds, table.seqColumnIds, id);
		});
	});
};
var removeColumnAction = createAction(ActionType.removeColumn);
var removeColumn = ({ collections, lww }, { payload: { id, tableId }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const tableCollection = query(collections).collection("tableEntities");
	const table = tableCollection.getOrCreate(tableId, (id) => createTable({ id }));
	query(collections).collection("tableColumnEntities").removeOperator(lww, safeVersion, id, () => {
		const index = table.columnIds.indexOf(id);
		if (index !== -1) tableCollection.updateOne(tableId, (table) => {
			table.columnIds.splice(index, 1);
		});
	});
};
var changeColumnNameAction = createAction(ActionType.changeColumnName);
var changeColumnName = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "name", () => {
		collection.updateOne(id, (column) => {
			column.name = value;
			column.ui.widthName = textInRange(toWidth(value));
		});
	});
};
var changeColumnCommentAction = createAction(ActionType.changeColumnComment);
var changeColumnComment = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "comment", () => {
		collection.updateOne(id, (column) => {
			column.comment = value;
			column.ui.widthComment = textInRange(toWidth(value));
		});
	});
};
var changeColumnDataTypeAction = createAction(ActionType.changeColumnDataType);
var DATA_TYPE = "dataType";
var changeColumnDataType = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	const column = collection.getOrCreate(id, (id) => createColumn({ id }));
	if ((lww[id]?.[3][DATA_TYPE] ?? -1) === safeVersion && value <= column.dataType) return;
	collection.replaceOperator(lww, safeVersion, id, DATA_TYPE, () => {
		collection.updateOne(id, (column) => {
			column.dataType = value;
			column.ui.widthDataType = textInRange(toWidth(value));
		});
	});
};
var changeColumnDefaultAction = createAction(ActionType.changeColumnDefault);
var changeColumnDefault = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "default", () => {
		collection.updateOne(id, (column) => {
			column.default = value;
			column.ui.widthDefault = textInRange(toWidth(value));
		});
	});
};
var changeColumnAutoIncrementAction = createAction(ActionType.changeColumnAutoIncrement);
var changeColumnAutoIncrement = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "options(autoIncrement)", () => {
		collection.updateOne(id, (column) => {
			column.options = value ? column.options | ColumnOption.autoIncrement : column.options & ~ColumnOption.autoIncrement;
		});
	});
};
var changeColumnPrimaryKeyAction = createAction(ActionType.changeColumnPrimaryKey);
var changeColumnPrimaryKey = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "options(primaryKey)", () => {
		collection.updateOne(id, (column) => {
			column.options = value ? column.options | ColumnOption.primaryKey : column.options & ~ColumnOption.primaryKey;
			column.ui.keys = value ? column.ui.keys | ColumnUIKey.primaryKey : column.ui.keys & ~ColumnUIKey.primaryKey;
		});
	});
};
var changeColumnUniqueAction = createAction(ActionType.changeColumnUnique);
var changeColumnUnique = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "options(unique)", () => {
		collection.updateOne(id, (column) => {
			column.options = value ? column.options | ColumnOption.unique : column.options & ~ColumnOption.unique;
		});
	});
};
var changeColumnNotNullAction = createAction(ActionType.changeColumnNotNull);
var changeColumnNotNull = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "options(notNull)", () => {
		collection.updateOne(id, (column) => {
			column.options = value ? column.options | ColumnOption.notNull : column.options & ~ColumnOption.notNull;
		});
	});
};
var changeColumnLogicalNameAction = createAction(ActionType.changeColumnLogicalName);
var changeColumnLogicalName = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "logicalName", () => {
		collection.updateOne(id, (column) => {
			column.logicalName = value;
			column.ui.widthLogicalName = textInRange(toWidth(value));
		});
	});
};
var changeColumnDomainAction = createAction(ActionType.changeColumnDomain);
var changeColumnDomain = (state, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(state.collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(state.lww, safeVersion, id, "domainId", () => {
		collection.updateOne(id, (column) => {
			relinkDomainColumn(state, id, column.domainId, value);
			column.domainId = value;
			column.ui.widthDomain = textInRange(toWidth(getLiveDomainName(state, value)));
		});
	});
};
var changeColumnNameDerivedAction = createAction(ActionType.changeColumnNameDerived);
var changeColumnNameDerived = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableColumnEntities");
	collection.getOrCreate(id, (id) => createColumn({ id }));
	collection.replaceOperator(lww, safeVersion, id, "options(nameDerived)", () => {
		collection.updateOne(id, (column) => {
			column.options = value ? column.options | ColumnOption.nameDerived : column.options & ~ColumnOption.nameDerived;
		});
	});
};
var moveColumnAction = createAction(ActionType.moveColumn);
var moveColumn = ({ collections }, { payload: { id, tableId, targetId } }) => {
	if (id === targetId) return;
	const tableCollection = query(collections).collection("tableEntities");
	const table = tableCollection.getOrCreate(tableId, (id) => createTable({ id }));
	const index = table.columnIds.indexOf(id);
	if (index === -1) return;
	const targetIndex = table.columnIds.indexOf(targetId);
	if (targetIndex === -1) return;
	tableCollection.updateOne(tableId, (table) => {
		table.columnIds.splice(index, 1);
		table.columnIds.splice(targetIndex, 0, id);
		const seqIndex = table.seqColumnIds.indexOf(id);
		const seqTargetIndex = table.seqColumnIds.indexOf(targetId);
		if (seqIndex !== -1 && seqTargetIndex !== -1) {
			table.seqColumnIds.splice(seqIndex, 1);
			table.seqColumnIds.splice(seqTargetIndex, 0, id);
		}
	});
};
var tableColumnReducers = {
	[ActionType.addColumn]: addColumn,
	[ActionType.removeColumn]: removeColumn,
	[ActionType.changeColumnName]: changeColumnName,
	[ActionType.changeColumnComment]: changeColumnComment,
	[ActionType.changeColumnDataType]: changeColumnDataType,
	[ActionType.changeColumnDefault]: changeColumnDefault,
	[ActionType.changeColumnAutoIncrement]: changeColumnAutoIncrement,
	[ActionType.changeColumnPrimaryKey]: changeColumnPrimaryKey,
	[ActionType.changeColumnUnique]: changeColumnUnique,
	[ActionType.changeColumnNotNull]: changeColumnNotNull,
	[ActionType.moveColumn]: moveColumn,
	[ActionType.changeColumnLogicalName]: changeColumnLogicalName,
	[ActionType.changeColumnDomain]: changeColumnDomain,
	[ActionType.changeColumnNameDerived]: changeColumnNameDerived
};
var actions = {
	addColumnAction,
	removeColumnAction,
	changeColumnNameAction,
	changeColumnCommentAction,
	changeColumnDataTypeAction,
	changeColumnDefaultAction,
	changeColumnAutoIncrementAction,
	changeColumnPrimaryKeyAction,
	changeColumnUniqueAction,
	changeColumnNotNullAction,
	moveColumnAction,
	changeColumnLogicalNameAction,
	changeColumnDomainAction,
	changeColumnNameDerivedAction
};
//#endregion
export { actions, addColumnAction, changeColumnAutoIncrementAction, changeColumnCommentAction, changeColumnDataTypeAction, changeColumnDefaultAction, changeColumnDomainAction, changeColumnLogicalNameAction, changeColumnNameAction, changeColumnNameDerivedAction, changeColumnNotNullAction, changeColumnPrimaryKeyAction, changeColumnUniqueAction, moveColumnAction, removeColumnAction, tableColumnReducers };
